import React, { useEffect, useState, useRef } from 'react';
import config from '../config.js';
import { detailToString } from '../utils/apiError';
import {
    finalizeEmailEdited, finalizeEmailHeaders, EDITED_EMAIL_NOTE,
    loadFinalizePreview, finalizePreviewState, finalizeProblem, finalizePayload, sendFinalize,
} from '../features/salesOrders/finalizeEmail';

// An edited email with no admin session: ask for an Admin PIN for this one send (verified by POST /auth/pin; never
// kept). Loaded on demand: adminSession pulls in the phone dialogs.
async function askAdminPinForEdit() {
    const { requestAdminPin } = await import('../adminSession.jsx');
    const admin = await requestAdminPin({ reason: 'Sending an edited customer email needs an Admin.' });
    return admin ? admin.pin : null;
}

const API_BASE = config.API_BASE_URL;

/**
 * SO-Finalize customer-email modal — used to send the consolidated
 * shipping/pickup manifest email at SO close-out. Mirrors the invoice
 * email modal in InvoicesModule (Send To single-select, CC multi-select
 * with checkboxes, "Send copy to info@" toggle, editable subject + body)
 * but lives in the packaging-plan flow.
 *
 * Props:
 *   show            - boolean, modal visibility
 *   onClose         - callback when user cancels (no API call fired)
 *   planId          - packaging plan id (drives the preview + send endpoints)
 *   kind            - 'shipping' (default) or 'pickup' — selects which
 *                     `/finalize-so-*` endpoint to POST to and changes button
 *                     wording. The preview endpoint always returns the
 *                     correct subject/body for the SO's actual fulfillment_type
 *                     so this prop is mostly cosmetic + endpoint routing.
 *   pin             - operator PIN (already collected by the caller). When
 *                     the dialog is opened directly without a PIN, an input
 *                     is rendered for it.
 *   onSent          - callback invoked with the API response on success.
 *                     Caller should refresh plan state + open the manifest.
 *   allowEdit       - default true. false (the Shipping hub's package guide before
 *                     its Office tools are on): the standard email only: the
 *                     subject, message and CC list are read-only, and the Send To
 *                     is fixed once the customer has a default recipient.
 *
 * The dialog owns its own preview fetch lifecycle so the caller doesn't
 * need to plumb contact data — pass the planId and the dialog handles it.
 */
export default function SoFinalizeEmailDialog({
    show,
    onClose,
    planId,
    kind,
    pin,
    onSent,
    orderNumber,
    // Manifest for the batch that was just shipped/staged (what the customer
    // email attached) — offered for printing here because the finalize
    // manifest spans EVERY pallet on the order, all ship dates.
    batchManifestUrl,
    batchPalletLabels,
    allowEdit = true,
}) {
    const [loading, setLoading]                 = useState(false);
    const [busy, setBusy]                       = useState('');
    const [error, setError]                     = useState('');
    const [preview, setPreview]                 = useState(null);
    const [selectedToId, setSelectedToId]       = useState('');
    const [ccContactIds, setCcContactIds]       = useState([]);
    const [sendCopyToSelf, setSendCopyToSelf]   = useState(false);
    const [subject, setSubject]                 = useState('');
    const [body, setBody]                       = useState('');
    const [pinInput, setPinInput]               = useState('');
    const lastLoadedPlanRef                     = useRef(null);

    const effectivePin = (pin || pinInput || '').trim();
    // The subject / body changed from the standard copy: jobq-api wants the email Admin PIN for that send.
    const edited = finalizeEmailEdited(preview, subject, body);
    const sendKind = kind === 'pickup' ? 'pickup' : 'shipping';

    // Lifecycle: load preview when the dialog opens for a new plan.
    useEffect(() => {
        if (!show || !planId) return;
        if (lastLoadedPlanRef.current === planId) return;  // already loaded
        lastLoadedPlanRef.current = planId;
        setLoading(true);
        setError('');
        setPreview(null);
        loadFinalizePreview(planId)
            .then((data) => {
                setPreview(data);
                setSelectedToId(data.default_to_contact_id || '');
                setCcContactIds(data.default_cc_contact_ids || []);
                setSubject(data.subject || '');
                setBody(data.body_text || '');
                setSendCopyToSelf(false);
            })
            .catch((err) => {
                setError(`Could not load email preview: ${err.message || String(err)}`);
            })
            .finally(() => setLoading(false));
    }, [show, planId]);

    // Reset on close so a re-open for a different plan starts fresh.
    useEffect(() => {
        if (show) return;
        lastLoadedPlanRef.current = null;
        setPreview(null);
        setSelectedToId('');
        setCcContactIds([]);
        setSendCopyToSelf(false);
        setSubject('');
        setBody('');
        setPinInput('');
        setError('');
        setBusy('');
    }, [show]);

    if (!show) return null;

    const titleKind = sendKind === 'pickup' ? 'Pickup' : 'Shipping';
    const orderLabel = preview?.order_number || (planId ? `Plan ${planId}` : '');

    // Notification scope from the preview (features/salesOrders/finalizeEmail.finalizePreviewState): 'none' = every
    // pallet the finalize covers was already emailed at ship/stage time, so finalizing records the close-out WITHOUT
    // a second, whole-order email; 'partial' = only the not-yet-notified pallets go out.
    const {
        contacts, customerFallback, hasAnyRecipientOption, emailScope, finalizeBlocked, showNoEmail, pendingPallets,
        notifiedSummary,
    } = finalizePreviewState(preview);
    const batchLabels = Array.isArray(batchPalletLabels) ? batchPalletLabels : [];

    // Opens the just-shipped batch manifest from a real click (popup-safe:
    // the tab is opened synchronously, then pointed at the fetched PDF).
    const openBatchManifest = async () => {
        if (!batchManifestUrl) return;
        const w = window.open('', '_blank');
        try {
            const res = await fetch(`${API_BASE}${batchManifestUrl}`);
            if (!res.ok) {
                const b = await res.json().catch(() => ({}));
                throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            if (w) {
                w.location.href = url;
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = `Manifest_${orderNumber || planId}_batch.pdf`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
            }
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (err) {
            if (w) { try { w.close(); } catch (_) { /* noop */ } }
            setError(`Could not open the manifest: ${err.message || String(err)}`);
        }
    };

    const handleSubmit = async (notify) => {
        setError('');
        const problem = finalizeProblem({ pin: effectivePin, notify, hasAnyRecipientOption, selectedToId });
        if (problem) {
            setError(problem);
            return;
        }
        // A changed subject / message needs the Admin PIN (X-Admin-Pin); the standard email goes on the worker PIN.
        const auth = await finalizeEmailHeaders({ notify, edited, askAdminPin: askAdminPinForEdit });
        if (auth.cancelled) {
            setError('Not sent: an edited email needs an Admin PIN. Use the standard text to send on your own PIN.');
            return;
        }
        setBusy(notify ? 'send' : 'skip');
        try {
            const payload = finalizePayload({
                pin: effectivePin, notify, selectedToId, customerFallback, ccContactIds, sendCopyToSelf, subject, body,
            });
            const responseBody = await sendFinalize({ planId, kind: sendKind, payload, headers: auth.headers });
            if (typeof onSent === 'function') {
                onSent(responseBody, { notify });
            }
        } catch (err) {
            setError(err.message || String(err));
        } finally {
            setBusy('');
        }
    };

    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header bg-success-subtle">
                        <h5 className="modal-title">
                            <i className="bi bi-envelope-paper me-2"></i>
                            Finalize SO {titleKind}: {orderLabel}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} disabled={!!busy}></button>
                    </div>
                    <div className="modal-body">
                        {loading && (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm text-primary"></div>
                                <span className="ms-2">Loading customer contacts…</span>
                            </div>
                        )}

                        {!loading && preview?.already_finalized && !preview?.batch_available && (
                            <div className="alert alert-warning">
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                This SO was already finalized
                                {preview.so_finalized_at && (<> at {new Date(preview.so_finalized_at).toLocaleString()}</>)}
                                {preview.so_finalized_by && (<> by {preview.so_finalized_by}</>)}.
                                Re-running will not send another email — use Reprint Manifest if you only need the PDF.
                            </div>
                        )}

                        {!loading && preview?.already_finalized && preview?.batch_available && (
                            <div className="alert alert-info">
                                <i className="bi bi-boxes me-1"></i>
                                This SO was finalized
                                {preview.so_finalized_at && (<> {new Date(preview.so_finalized_at).toLocaleString()}</>)}
                                {preview.so_finalized_by && (<> by {preview.so_finalized_by}</>)},
                                but <strong>{(preview.batch_pallets || []).length} pallet(s)
                                ({(preview.batch_pallets || []).join(', ')}) went out since then</strong> as a new batch.
                                Sending emails the customer a manifest covering <strong>just this new batch</strong> and
                                updates the finalize stamp. Edit the subject/body below if the batch was delivered
                                rather than picked up.
                            </div>
                        )}

                        {!loading && batchManifestUrl && (
                            <div className="d-flex align-items-center justify-content-between border rounded p-2 mb-3 bg-light">
                                <span className="small">
                                    <i className="bi bi-printer me-1"></i>
                                    <strong>This shipment's manifest</strong>
                                    {batchLabels.length > 0
                                        ? <> — the {batchLabels.length} pallet(s) just {sendKind === 'pickup' ? 'staged' : 'shipped'} ({batchLabels.join(', ')}), the document the customer's email attached.</>
                                        : <> — just the pallets that went out in this click, the document the customer's email attached.</>}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-dark ms-2 text-nowrap"
                                    onClick={openBatchManifest}
                                    disabled={!!busy}
                                >
                                    <i className="bi bi-printer me-1"></i>Print this shipment
                                </button>
                            </div>
                        )}

                        {showNoEmail && (
                            <div className="alert alert-success">
                                <i className="bi bi-check2-circle me-1"></i>
                                <strong>Nothing left to email.</strong> The customer was already emailed about every pallet in this
                                {preview?.already_finalized ? ' new batch' : ' shipment'}
                                {notifiedSummary ? <> ({notifiedSummary})</> : null}.
                                Finalizing records the close-out and opens the{' '}
                                {preview?.already_finalized ? 'new-batch manifest' : 'consolidated manifest — every pallet on the order, all ship dates —'}{' '}
                                for your records. <strong>No email will be sent.</strong>
                            </div>
                        )}

                        {!loading && preview && !finalizeBlocked && emailScope === 'partial' && (
                            <div className="alert alert-info">
                                <i className="bi bi-envelope me-1"></i>
                                This email covers only the <strong>{pendingPallets.length} pallet(s) the customer has not been
                                emailed about yet</strong> ({pendingPallets.join(', ')}); the attached manifest is limited to them.
                                {notifiedSummary ? <> Already sent: {notifiedSummary}.</> : null}
                            </div>
                        )}

                        {!loading && preview && showNoEmail && !pin && (
                            <div className="mb-3">
                                <label className="form-label">PIN</label>
                                <input
                                    type="password"
                                    inputMode="numeric"
                                    className="form-control"
                                    value={pinInput}
                                    onChange={(e) => setPinInput(e.target.value)}
                                    placeholder="4-digit PIN"
                                    maxLength={6}
                                    autoComplete="off"
                                />
                            </div>
                        )}

                        {!loading && preview && !showNoEmail && (
                            <>
                                <p className="text-muted mb-3">
                                    Sending the <strong>{titleKind.toLowerCase()}</strong> manifest email for{' '}
                                    <strong>{preview.customer_name || 'customer'}</strong>.{' '}
                                    {emailScope === 'partial'
                                        ? <>A manifest covering the {pendingPallets.length} pallet(s) not yet emailed will be attached automatically.</>
                                        : <>The manifest PDF will be attached automatically.</>}
                                </p>

                                {/* Send To */}
                                <div className="mb-3">
                                    <label className="form-label">Send To</label>
                                    <select
                                        className="form-select"
                                        value={selectedToId}
                                        disabled={!allowEdit && !!preview.default_to_contact_id}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setSelectedToId(v);
                                            // Drop the To from the CC list so it can't double-fire.
                                            setCcContactIds((prev) => prev.filter((id) => String(id) !== String(v)));
                                        }}
                                    >
                                        {!hasAnyRecipientOption && (
                                            <option value="">— No contacts with email on file —</option>
                                        )}
                                        {contacts.map((c) => (
                                            <option key={c.contact_id} value={c.contact_id}>
                                                {c.name} ({c.email}){c.role ? ` — ${c.role}` : ''}
                                            </option>
                                        ))}
                                        {customerFallback && (
                                            <option value="__customer_default__">
                                                {(customerFallback.name || 'Customer')} ({customerFallback.email}) — customer default
                                            </option>
                                        )}
                                    </select>
                                    {!hasAnyRecipientOption && (
                                        <div className="form-text text-warning">
                                            Add a contact (with email) on the customer record to enable sending.
                                            You can still finalize without an email.
                                        </div>
                                    )}
                                </div>

                                {/* CC */}
                                <div className="mb-3">
                                    <label className="form-label">CC Additional Recipients</label>
                                    <div
                                        className="border rounded px-2 py-2"
                                        style={{ maxHeight: '160px', overflowY: 'auto', background: '#fafafa' }}
                                    >
                                        {contacts.filter((c) => String(c.contact_id) !== String(selectedToId)).length === 0 ? (
                                            <div className="text-muted small">No additional contacts available.</div>
                                        ) : (
                                            contacts
                                                .filter((c) => String(c.contact_id) !== String(selectedToId))
                                                .map((c) => {
                                                    const checked = ccContactIds.includes(c.contact_id);
                                                    return (
                                                        <div className="form-check" key={c.contact_id}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                id={`cc-${c.contact_id}`}
                                                                checked={checked}
                                                                disabled={!allowEdit}
                                                                onChange={(e) => {
                                                                    setCcContactIds((prev) => {
                                                                        if (e.target.checked) return [...prev, c.contact_id];
                                                                        return prev.filter((id) => id !== c.contact_id);
                                                                    });
                                                                }}
                                                            />
                                                            <label className="form-check-label" htmlFor={`cc-${c.contact_id}`}>
                                                                {c.name} ({c.email}){c.role ? ` — ${c.role}` : ''}
                                                            </label>
                                                        </div>
                                                    );
                                                })
                                        )}
                                    </div>
                                </div>

                                {/* Send copy to self */}
                                <div className="form-check mb-3">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="copy-to-self"
                                        checked={sendCopyToSelf}
                                        onChange={(e) => setSendCopyToSelf(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="copy-to-self">
                                        Send a copy to <code>info@kancraftmfg.com</code>
                                    </label>
                                </div>

                                {/* Subject */}
                                <div className="mb-3">
                                    <label className="form-label">Subject</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={subject}
                                        readOnly={!allowEdit}
                                        onChange={(e) => setSubject(e.target.value)}
                                    />
                                </div>

                                {/* Body */}
                                <div className="mb-3">
                                    <label className="form-label">Message Body</label>
                                    <textarea
                                        className="form-control"
                                        rows={8}
                                        value={body}
                                        readOnly={!allowEdit}
                                        onChange={(e) => setBody(e.target.value)}
                                    />
                                    {!allowEdit && (
                                        <div className="form-text" data-testid="finalize-standard-only">
                                            The standard email goes as it is. Changing the recipients or the text is an office tool.
                                        </div>
                                    )}
                                    <div className="form-text">
                                        The unified manifest PDF will be attached to this email automatically.
                                    </div>
                                    {edited && (
                                        <div className="form-text text-warning d-flex align-items-start gap-2" data-testid="finalize-edited-note">
                                            <span>{EDITED_EMAIL_NOTE}</span>
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 text-nowrap"
                                                onClick={() => { setSubject(preview?.subject || ''); setBody(preview?.body_text || ''); }}
                                                disabled={!!busy}
                                            >
                                                Use the standard text
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* PIN (only when caller didn't supply one) */}
                                {!pin && (
                                    <div className="mb-3">
                                        <label className="form-label">PIN</label>
                                        <input
                                            type="password"
                                            inputMode="numeric"
                                            className="form-control"
                                            value={pinInput}
                                            onChange={(e) => setPinInput(e.target.value)}
                                            placeholder="4-digit PIN"
                                            maxLength={6}
                                            autoComplete="off"
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {error && (
                            <div className="alert alert-danger mt-2 mb-0 py-2">
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                {error}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={!!busy}
                        >
                            Cancel
                        </button>
                        <button
                            className={showNoEmail ? 'btn btn-success' : 'btn btn-outline-secondary'}
                            onClick={() => handleSubmit(false)}
                            disabled={!!busy || loading || finalizeBlocked}
                            title={showNoEmail
                                ? 'Record the close-out; the customer was already notified for every pallet'
                                : 'Finalize the SO without sending the customer email'}
                        >
                            {busy === 'skip'
                                ? 'Finalizing…'
                                : showNoEmail
                                    ? <><i className="bi bi-flag-fill me-1"></i>{preview?.already_finalized ? 'Re-finalize (no email needed)' : 'Finalize (no email needed)'}</>
                                    : 'Finalize without email'}
                        </button>
                        {!showNoEmail && (
                        <button
                            className="btn btn-success"
                            onClick={() => handleSubmit(true)}
                            disabled={
                                !!busy
                                || loading
                                || finalizeBlocked
                                || !hasAnyRecipientOption
                                || !selectedToId
                            }
                        >
                            <i className="bi bi-send me-1"></i>
                            {busy === 'send'
                                ? 'Sending…'
                                : (preview?.already_finalized && preview?.batch_available)
                                    ? 'Send batch manifest & re-finalize'
                                    : (sendKind === 'pickup' ? 'Send & Finalize Pickup' : 'Send & Finalize Shipment')}
                        </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
